import { Link, navigate } from "@reach/router"
// Styles
import { Breadcrumb_s, CashFlowMapTable_s } from "components/asset-management/chart/chart-styles"
import { Errors } from "components/asset-management/errors"
// Components
import CarouselAutomation from "components/carousel-automation"
import { Toolbar } from "components/subnav"
import { DetailedRouteUpDown_s, DetailedUpDownBtn_s } from "components/table/data-table-styles"
import { DropdownField } from "components/table/display"
import { DropdownEdit, TextEdit } from "components/table/inputs"
import { field as _field } from "components/table/lib/column-field"
import SearchHeader from "components/table/search-header"
import {
  TableAutomation
} from "components/table/table-automation"
import { HeaderIcon_s } from "components/wrapper/header/header-styles"
// Data
import cashFlowMapService from "data/cash-flow-map"
import chartService, { Chart } from "data/chart"
import keyService, * as chord from "data/key"
import summaryAccountService from "data/summaryaccount"
import validationService from "data/validate"
import { BackArrowIcon, CaretRightIcon } from 'images/icons/general/'
// Icons
import { CaretDownIcon, CaretUpIcon } from "images/icons/tools/"
import _ from "lodash"
import { Column } from "primereact/column"
import { ContextMenu } from "primereact/contextmenu"
// PR
import { DataTable } from "primereact/datatable"
import React, { useEffect, useRef, useState } from "react"
import Isvg from 'react-inlinesvg'
import { InputText_s } from "styles/input-styles"


type ContextRow = {
  cashflowmapid: number
  sortorder?: number
  systeminput?: string
}

const tableId = "cashFlowMapTable"

const CashFlowMap = (props) => {
  const usePrevious = (value) =>{
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const [charts, setCharts] = useState<Chart[]|null>(null);
  const [currentChart, setCurrentChart] = useState<Chart|null>(null);
  const [cashFlowMaps, setCashFlowMaps] = useState<object[]|null>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [searchFilter, setSearchFilter] = useState<string>("")
  const prevSearch = usePrevious(searchFilter);
  const [automationActive, setAutomationActive] = useState<boolean>(false);
  const [tableAutomationActive, setTableAutomationActive] = useState<boolean>(true);
  const [summaryAccountFilter, setSummaryAccountFilter] = useState<string>("");
  const [cashFlowLineItemFilter, setCashFlowLineItemFilter] = useState<string>("");
  const [accountcategoryFilter, setAccountcategoryFilter] = useState<string>("All");
  const [propertytypeFilter, setpropertytypeFilter] = useState<string>("All");
  const [summaries, setSummaries] = useState<object[]|null>([]);
  const [_errors, setErrors] = useState<any>()
  const tableRef = useRef() as React.MutableRefObject<DataTable>
  const field = _field('cashflowmap')

  const accountCategoryOptions = [{value: 'Revenue', label: 'Revenue'},{value: 'Operating Expenses', label: 'Operating Expenses'},{value: 'Capital Expenses', label: 'Capital Expenses'}]
  const propertyTypeOptions = [
    {value: 'Commercial', label: 'Commercial'},
    {value: 'Hospitality', label: 'Hospitality'},
    {value: 'Multifamily', label: 'Multifamily'}
  ]

  const chartid = currentChart?.chartid
  useEffect(() => {
    if(!chartid) return
    validationService.validateByParent('cashflowmap', 'chart', chartid)
  },[chartid])

  useEffect(() => {
    if (prevSearch !== "" && searchFilter === "") _.invoke(tableRef, 'current.filter', '', 'contains');
    else if (searchFilter !== "") _.invoke(tableRef, 'current.filter', searchFilter, 'contains');
    else return
  }, [searchFilter])

  useEffect(() => {
    const chartSubscription = chartService.stream().subscribe((data) => {
      if (data.state === "ok"){
        setCharts(data.value)
        setCurrentChart(_.find(data.value, {chartid: parseInt(props.chartId)}))
      }
    })

    const cashFlowMapSubscription = cashFlowMapService.stream().subscribe((data) => {
      if (data.state === "ok"){
        setCashFlowMaps(data.value)
      }
    })

    const summaryAccountSubscription = summaryAccountService.stream().subscribe((data) => {
      if (data.state === "ok"){
        // map summary propertytype array to dropdown friendly version
        const s = _.map(_.cloneDeep(data.value), (val, idx) => {
          if(_.isArray(val.propertytype)) {
            val.propertytype = `{${_.join(val.propertytype,',')}}`
          }
          return val
        })
        setSummaries(s)
      }
    })

    const validationSubscription = validationService.stream().subscribe((e) => {
      setErrors(e)
    })

    chartService.fetchCharts()
    cashFlowMapService.fetchCashFlowMaps(parseInt(props.chartId))
    summaryAccountService.fetchSummaryAccounts(parseInt(props.chartId))

    return function cleanup() {
      chartSubscription.unsubscribe()
      cashFlowMapSubscription.unsubscribe()
      summaryAccountSubscription.unsubscribe()
      validationSubscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    setTableAutomationActive(true)
    setAutomationActive(false)
    if (currentChart){
      cashFlowMapService.fetchCashFlowMaps(currentChart['chartid'])
      summaryAccountService.fetchSummaryAccounts(currentChart['chartid'])
    }

    const filter = (errors) => {
      if (!currentChart) return errors
      const chartid = _.get(currentChart, 'chartid')
      if (!chartid) return errors
      let { chart, cashflowmap, detailaccount, summaryaccount } = _.cloneDeep(_.pick(errors.errordetail, 'chart', 'summaryaccount', 'cashflowmap', 'detailaccount'))

      if (chart) chart = {[chartid]: chart[chartid] || []}

      for (let table of [cashflowmap, detailaccount, summaryaccount]) {
        for (let id in (table||{})){
          if (table[id][0]['_object'].chartid !== chartid) {
            delete table[id]
          }
        }
      }

      return {
        ...errors,
        errordetail: {chart, cashflowmap, detailaccount, summaryaccount},
        calcDetail: undefined
      }
    }
    validationService.addFilter(filter)
    return () => validationService.removeFilter(filter)

  }, [currentChart])

  useEffect(() => {
    const keySubscription = keyService.stream().subscribe(k => {
      switch(true) {
        case k.chord === chord.CTRL_SHIFT_UP && k.down === true:
          toggleChart('up')
          break
        case k.chord === chord.CTRL_SHIFT_DOWN && k.down === true:
          toggleChart('down')
          break
        case k.chord === chord.CTRL_SHIFT_BACK && k.down === true:
          navigate('/n/asset-management/chart')
      }
    })

    return function cleanup() {
      keySubscription.unsubscribe()
    }
  })

  useEffect(() => {
    filterData();
  }, [cashFlowMaps, accountcategoryFilter, propertytypeFilter, cashFlowLineItemFilter, summaryAccountFilter])

  function filterData():void {
    let nextData = _.cloneDeep(cashFlowMaps);

    if (!_.isNil(summaryAccountFilter) && summaryAccountFilter !== ""){
      nextData = nextData.filter(item => _.get(item, 'summaryaccountid')
                 && _.find(allSummaryAccountOptions, { value: _.get(item, 'summaryaccountid')})['label']
                 .toLowerCase()
                 .includes(summaryAccountFilter.toLowerCase()))
    }

    if (cashFlowLineItemFilter !== ""){
      nextData = nextData.filter(item => _.get(item, 'cashflowlineitem')?.toLowerCase()
                .includes(cashFlowLineItemFilter.toLowerCase()))
    }

    if (accountcategoryFilter !== "All"){
      nextData = nextData.filter(item => _.get(item, 'accountcategory')
                 && _.find(accountCategoryOptions, { value: _.get(item, 'accountcategory')})['label'].toLowerCase()
                 .includes(accountcategoryFilter.toLowerCase()))
    }

    if (propertytypeFilter !== "All"){
      nextData = nextData.filter(item => _.get(item, 'propertytype')
                 && _.find(propertyTypeOptions, { value: _.get(item, 'propertytype')})['value'].toLowerCase()
                .includes(propertytypeFilter.toLowerCase()))
    }

    setFilteredData(nextData.map(d => { return {...d, systeminput: 'system'} }))
  }

  const persist = (value, field, rowData) => {
    let cashFlowMap = _.cloneDeep(rowData)
    _.set(cashFlowMap, field, value);
    delete cashFlowMap['systeminput']
    cashFlowMapService.patch(cashFlowMap);
  }

  function toggleChart(direction:string):void {
    const currentChartIdx       = charts?.indexOf(currentChart!);
    let   nextChart:Chart|null  = null
    if (direction === 'up' && charts){
      nextChart = charts && charts[currentChartIdx! - 1]
    } else {
      nextChart = charts && charts[currentChartIdx! + 1]
    }
    if (nextChart) setCurrentChart(nextChart)
  }

  const rowClassName = (rowData) => {
    if (rowData.systeminput && rowData.systeminput === 'system'){
      return {'system-row' : rowData.systeminput === 'system'}
    }
    else return {'' : rowData}
  }

  const getSummaryAccountOptions = (rowData) => {
    let summaryAccountOptions = summaries?.filter(s => s['accountcategory'] === rowData.accountcategory && s['propertytype']?.includes(rowData.propertytype))
                                          .map(s => { return { 'label': `${s['accountnumber'] || ''} - ${s['description'] || ''}`, 'value': s['summaryaccountid'] }})
    return summaryAccountOptions
  }
  const allSummaryAccountOptions = summaries?.map(s => { return { 'label': `${s['accountnumber']} - ${s['description']}`, 'value': s['summaryaccountid'] }})

  return (
    <div>
      <Toolbar>
        { currentChart &&
          <Breadcrumb_s>
            <Link to="/n/asset-management/chart">
              <HeaderIcon_s style={{marginRight: '5px'}}><Isvg src={BackArrowIcon} /></HeaderIcon_s>
              Chart
            </Link>
            <HeaderIcon_s style={{marginRight: '5px'}}><Isvg src={CaretRightIcon} /></HeaderIcon_s>
            <div>Cash Flow Map</div>
            <DetailedRouteUpDown_s>
              <DetailedUpDownBtn_s id="detailedRouteRecordUp" disabled={!charts || _.isNil(charts[charts.indexOf(currentChart!) - 1])} onClick={()=>toggleChart('up')}><Isvg src={CaretUpIcon} /></DetailedUpDownBtn_s>
              <DetailedUpDownBtn_s id="detailedRouteRecordDown" disabled={!charts || _.isNil(charts[charts.indexOf(currentChart!) + 1])} onClick={()=>toggleChart('down')}><Isvg src={CaretDownIcon} /></DetailedUpDownBtn_s>
            </DetailedRouteUpDown_s>
            <div>{_.get(currentChart, "code")}</div>
          </Breadcrumb_s>
        }
        <Errors />
      </Toolbar>
      <SearchHeader setSearchFilter={setSearchFilter} />
      <ContextMenu appendTo={document.querySelector("body")} onHide={() => {}}/>
      <CashFlowMapTable_s>
        <DataTable
          globalFilter={searchFilter}
          id={tableId}
          ref={tableRef}
          value={filteredData}
          paginator={true}
          rows={20}
          rowsPerPageOptions={[10, 20, 50, 100, 500]}
          scrollable={true}
          paginatorPosition="bottom"
          rowClassName={rowClassName}
          stateStorage="custom"
          customSaveState={state => localStorage.setItem("primereact-table-preferences", JSON.stringify({rows: state.rows}))}
          customRestoreState={() => JSON.parse(localStorage.getItem("primereact-table-preferences")!)}>
          <Column style={{maxWidth: "300px", minWidth: "300px"}} className="p-editable-column"
            filter
            sortable
            header="Summary Account #"
            {...field('summaryaccountid', (rowData, props) => DropdownField({options: getSummaryAccountOptions(rowData), rowData, persist, ...props}))}
            editor={props => <DropdownEdit options={getSummaryAccountOptions(props.rowData)} persist={persist} {...props} />}
            filterElement={
              <InputText_s value={summaryAccountFilter} onChange={(e) => setSummaryAccountFilter(e.target.value)} />
            }
          />
          <Column style={{minWidth: "300px"}} className="p-editable-column system-disabled"
            filter
            sortable
            header="Cash Flow Line Item"
            {...field('cashflowlineitem')}
            editor={props => TextEdit({...props})}
            filterElement={
              <InputText_s value={cashFlowLineItemFilter} onChange={(e) => setCashFlowLineItemFilter(e.target.value)} />
            }
          />
          <Column style={{minWidth: "180px"}} className="p-editable-column system-disabled"
            filter
            sortable
            header="Account Category"
            {...field('accountcategory')}
            filterElement={
              <DropdownEdit
                field="accountcategoryFilter"
                rowData={{accountcategoryFilter}}
                options={[{value:"All", label:"All"}, ...accountCategoryOptions]}
                persist={(val) => setAccountcategoryFilter(val)}
              />
            }
          />
          <Column style={{minWidth: "150px"}} className="p-editable-column system-disabled"
            filter
            sortable
            header="Property Type"
            {...field('propertytype')}
            filterElement={
              <DropdownEdit
                field="propertytypeFilter"
                rowData={{propertytypeFilter}}
                options={[{value:"All", label:"All"}, ...propertyTypeOptions]}
                persist={(val) => setpropertytypeFilter(val)}
              />
            }
          />
        </DataTable>
        { filteredData && filteredData.length
          ? <div>
              <CarouselAutomation outsideTable={true} automationActive={automationActive} editableFilters={true} includeSearchBar={true} tabOut={() => { setAutomationActive(false); setTableAutomationActive(true) }} />
              <TableAutomation tableId={tableId} automationActive={tableAutomationActive} shiftTabOut={() => setTableAutomationActive(false)}/>
            </div>
          : null
        }
      </CashFlowMapTable_s>
    </div>
  )
}

export default CashFlowMap

